/* eslint-disable react/static-property-placement */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import NumberFormat from 'react-number-format'
import { midas } from '@resurs/midas'
import { LocaleState } from '@merchant-portal/merchant-portal-base'
import { getCurrencySymbol } from '../../models/countries'
import { getText } from '../TextLabels'

export default class CurrencyInput extends Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    currency: PropTypes.string,
    required: PropTypes.string,
    label: PropTypes.string,
    allowDecimals: PropTypes.bool,
    thousandSeparator: PropTypes.string,
    help: PropTypes.string,
    placeholderRange: PropTypes.bool,
    hint: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    boundaries: PropTypes.shape({
      min: PropTypes.shape({
        value: PropTypes.number,
        error: PropTypes.string,
      }),
      max: PropTypes.shape({
        value: PropTypes.number,
        error: PropTypes.string,
      }),
    }),
    onValueChange: PropTypes.func,
    onChange: PropTypes.func,
    isValid: PropTypes.func,
    value: PropTypes.number,
    wrapLabel: PropTypes.bool,
    showOnlyCurrencyNoRange: PropTypes.bool,
  }

  static defaultProps = {
    disabled: false,
    required: null,
    label: null,
    currency: '',
    hint: null,
    allowDecimals: false,
    placeholderRange: false,
    thousandSeparator: ' ',
    help: null,
    boundaries: {
      min: { value: null, error: '' },
      max: { value: null, error: '' },
    },
    onValueChange: () => {},
    onChange: () => {},
    isValid: () => {},
    value: null,
    wrapLabel: false,
    showOnlyCurrencyNoRange: false,
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    //  If upper boundry changes to a higher val than the current (as in debit flow)
    //  remove the boundry max error
    const state = { boundaries: nextProps.boundaries }
    if (prevState.vals && nextProps.boundaries) {
      if (prevState.vals.floatValue <= nextProps.boundaries.max.value) {
        return { ...state, boundryErrorMax: null }
      }
      return { ...state, boundryErrorMax: nextProps.boundaries.max.error }
    }
    return state
  }

  constructor(props) {
    super(props)
    this.state = {
      required: false,
      dirty: false,
      boundryErrorMax: null,
      boundryErrorMin: null,
      vals: null,
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.boundaries.max.value !== this.props.boundaries.max.value &&
      this.state.vals
    ) {
      if (this.state.vals.floatValue > this.props.boundaries.max.value) {
        this.props.isValid({ key: this.props.id, valid: false })
      } else {
        this.props.isValid({ key: this.props.id, valid: true })
      }
    }
  }

  handleInvalid(e) {
    e.preventDefault()
    this.setState({ required: true })
  }

  handleBlur() {
    this.setState({ dirty: true })
  }

  test(vals) {
    this.props.onValueChange(vals)

    let valid = false

    if (!Number.isNaN(+vals.floatValue)) {
      // eslint-disable-next-line
      this.setState({ vals, required: false }) // need vals for prevState

      const { floatValue } = vals

      const { min, max } = this.props.boundaries

      if (!Number.isNaN(min.value) && max.value) {
        if (floatValue < min.value) {
          this.props.isValid({ key: this.props.id, valid })
          return this.setState({
            boundryErrorMin: min.error,
            boundryErrorMax: null,
          })
        }
        if (floatValue > max.value) {
          this.props.isValid({ key: this.props.id, valid })
          return this.setState({
            boundryErrorMax: max.error,
            boundryErrorMin: null,
          })
        }
        this.setState({ boundryErrorMin: null, boundryErrorMax: null })
        valid = true
      }
    } else {
      valid = false
    }
    this.props.isValid({
      key: this.props.id,
      valid,
    })
    return null
  }

  render() {
    const {
      id,
      disabled,
      required,
      label,
      hint,
      allowDecimals,
      placeholderRange,
      thousandSeparator,
      boundaries,
      // eslint-disable-next-line no-unused-vars
      decimalSeparator,
      // eslint-disable-next-line no-unused-vars
      isValid, // ? prevent passing to NumberFormat, therefore destructured but not used
      value,
      help,
      wrapLabel,
      showOnlyCurrencyNoRange,
      ...other
    } = this.props

    const { country, language } = this.context

    const { boundryErrorMin, boundryErrorMax, dirty } = this.state

    const labelText = label ? (
      <label className={midas.formLabel} htmlFor={id}>
        {label}
        {required && <span> *</span>}
      </label>
    ) : null

    const errorText =
      boundryErrorMin || boundryErrorMax ? (
        <label className={midas.formInputHint}>
          {boundryErrorMin && (
            <div>
              <span>{`${boundryErrorMin} `}</span>
              <NumberFormat
                displayType="text"
                thousandSeparator={thousandSeparator}
                renderText={val => getText('default.currency', val)}
                value={boundaries.min.value}
              />
            </div>
          )}
          {boundryErrorMax && (
            <div>
              <span>{`${boundryErrorMax} `}</span>
              <NumberFormat
                displayType="text"
                thousandSeparator={thousandSeparator}
                renderText={val => getText('default.currency', val)}
                value={boundaries.max.value}
              />
            </div>
          )}
        </label>
      ) : null

    const requiredText = this.state.required ? (
      <label className={midas.formInputHint}>{required}</label>
    ) : null

    const hintText = hint ? (
      <label className={midas.formInputHint}>{hint}</label>
    ) : null

    const helpText = help ? (
      <label className={midas.formLabel}>{help}</label>
    ) : null

    const wrapperClassNames = [
      midas.formGroup,
      this.state.required && midas.hasWarning,
      (this.state.boundryErrorMax || this.state.boundryErrorMin) &&
        this.state.dirty &&
        midas.hasDanger,
      this.state.dirty &&
        !this.state.required &&
        !this.state.boundryErrorMax &&
        !this.state.boundryErrorMin &&
        midas.hasSuccess,
    ]
      .filter(val => val)
      .join(' ')

    const classNames = [midas.formInput].filter(val => val).join(' ')

    const getPlaceholderText = () => {
      if (showOnlyCurrencyNoRange) {
        return getCurrencySymbol(country)
      }

      if (placeholderRange) {
        return getCurrencySymbol(country).wrapRange(
          boundaries.min.value.toLocaleString(language),
          boundaries.max.value?.toLocaleString(language) || '',
        )
      }

      return undefined
    }

    const props = {
      ...other,
      id,
      value: value > 0 ? value : undefined,
      disabled,
      required,
      suffix: getText('default.currency'),
      thousandSeparator,
      allowNegative: false,
      type: 'tel',
      decimalScale: allowDecimals ? 2 : 0,
      decimalSeparator: ',',
      placeholder: getPlaceholderText(),
      onValueChange: vals => this.test(vals),
      getInputRef: element => {
        // eslint-disable-next-line react/no-unused-class-component-methods
        this.inputElement = element
      },
      onInvalid: e => this.handleInvalid(e),
      onBlur: e => {
        this.handleBlur()
        if (required && e.target.value === '') {
          this.handleInvalid(e)
        }
      },
    }

    return (
      <div
        className={wrapperClassNames}
        style={!wrapLabel ? { whiteSpace: 'nowrap' } : {}}
      >
        {labelText}
        <NumberFormat className={classNames} {...props} />
        {dirty && errorText}
        {requiredText && !helpText}
        {!errorText && !requiredText && hintText}
        {helpText}
      </div>
    )
  }
}

CurrencyInput.contextType = LocaleState
